import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Divider, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CustomButton from '../../../../components/buttons/CustomButton';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import ModuleCard from '../../../appointment/components/moduleCard/ModuleCard';
import PatientExportDialog from '../../components/PatientExportDialog';
import PatientAnonymizeDialog from '../../components/PatientAnonymizeDialog';
import PatientDeleteDialog from '../../components/PatientDeleteDialog';
import AnonymizedBadge from '../../components/AnonymizedBadge';

const ROW_SX = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '14px 0',
    '&:first-of-type': { paddingTop: 0 },
    '&:last-of-type': { paddingBottom: 0 },
};

const ROW_ACTION_SX = {
    flexShrink: 0,
    '@media (max-width: 600px)': {
        width: '100%',
        '& > .MuiButton-root': { width: '100%' },
    },
};

// Botão vermelho contornado: o tema força contained/outlined em azul, então o
// estilo vai por sx (espelha o root do MuiButton do tema).
const DANGER_BUTTON_SX = {
    color: 'error.main',
    borderColor: 'error.main',
    borderRadius: '8px',
    textTransform: 'none',
    height: '40px',
    fontWeight: 500,
    '&:hover': {
        borderColor: 'error.main',
        backgroundColor: ColorPalette.errorLight,
    },
};

function PrivacyRow({ icon, iconColor, iconBackground, title, description, action }) {
    return (
        <Box sx={ROW_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: '240px' }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: iconColor,
                        backgroundColor: iconBackground,
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: ColorPalette.textDark }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: ColorPalette.textLight }}>
                        {description}
                    </Typography>
                </Box>
            </Box>
            <Box sx={ROW_ACTION_SX}>{action}</Box>
        </Box>
    );
}

/**
 * Card "Privacidade e dados" na seção Dados pessoais: direitos do titular
 * (LGPD) sobre este paciente. Quem decide se aparece é o PersonalInfo
 * (só o dono da clínica). Os diálogos ficam sempre montados, fora do card;
 * só o `open` alterna, para o Toast de sucesso sobreviver à recarga do paciente.
 * Especificação: docs/features/lgpd-eliminacao/README.md
 */
function PatientPrivacyPanel({ patient, onChange }) {
    const navigate = useNavigate();
    const [exportOpen, setExportOpen] = useState(false);
    const [anonymizeOpen, setAnonymizeOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const isAnonymized = !!patient.anonymized_at;
    const isDeleted = !!patient.deleted_at;

    return (
        <>
            <ModuleCard
                title="Privacidade e dados"
                icon={<ShieldOutlinedIcon />}
                description="Direitos do titular previstos na LGPD. Só para quem é proprietário(a) da conta."
                accent="purple"
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <PrivacyRow
                        icon={<FactCheckOutlinedIcon fontSize="small" />}
                        iconColor={ColorPalette.blueMain}
                        iconBackground={ColorPalette.blueLight4}
                        title="Histórico de acessos"
                        description="Quem abriu, alterou ou baixou dados deste paciente, com data e usuário."
                        action={
                            <CustomButton
                                label="Ver histórico"
                                style="primary"
                                variant="outlined"
                                icon={<FactCheckOutlinedIcon fontSize="small" />}
                                onClick={() => navigate(`/company/audit?patient=${patient.id}`)}
                            />
                        }
                    />
                    <Divider />
                    <PrivacyRow
                        icon={<FileDownloadOutlinedIcon fontSize="small" />}
                        iconColor={ColorPalette.blueMain}
                        iconBackground={ColorPalette.blueLight4}
                        title="Exportar dados"
                        description="Baixa um arquivo ZIP com cadastro, histórico, documentos e anexos deste paciente."
                        action={
                            <CustomButton
                                label="Exportar dados"
                                style="primary"
                                variant="outlined"
                                icon={<FileDownloadOutlinedIcon fontSize="small" />}
                                onClick={() => setExportOpen(true)}
                            />
                        }
                    />
                    <Divider />
                    <PrivacyRow
                        icon={<PersonOffOutlinedIcon fontSize="small" />}
                        iconColor={ColorPalette.error}
                        iconBackground={ColorPalette.errorLight}
                        title="Anonimizar paciente"
                        description={isAnonymized
                            ? "Os dados pessoais deste paciente já foram removidos."
                            : "Remove nome, CPF, contatos e endereço de forma irreversível. O histórico clínico e financeiro permanece sem identificação."}
                        action={isAnonymized ? (
                            <AnonymizedBadge anonymizedAt={patient.anonymized_at} />
                        ) : (
                            <Button
                                variant="outlined"
                                disableElevation
                                startIcon={<PersonOffOutlinedIcon fontSize="small" />}
                                onClick={() => setAnonymizeOpen(true)}
                                sx={DANGER_BUTTON_SX}
                            >
                                Anonimizar paciente
                            </Button>
                        )}
                    />
                    <Divider />
                    <PrivacyRow
                        icon={<DeleteOutlineIcon fontSize="small" />}
                        iconColor={ColorPalette.error}
                        iconBackground={ColorPalette.errorLight}
                        title="Excluir paciente"
                        description={isDeleted
                            ? "Os dados deste paciente foram excluídos. Restaram apenas os registros financeiros e de agenda, sem identificação."
                            : "Apaga os dados deste paciente da Gabriela: anamneses, histórico, fotos, anexos e documentos. Agendamentos passados, financeiro e notas fiscais ficam sem identificação."}
                        action={isDeleted ? (
                            <AnonymizedBadge anonymizedAt={patient.deleted_at} label="Excluído em" />
                        ) : (
                            <Button
                                variant="outlined"
                                disableElevation
                                startIcon={<DeleteOutlineIcon fontSize="small" />}
                                onClick={() => setDeleteOpen(true)}
                                sx={DANGER_BUTTON_SX}
                            >
                                Excluir paciente
                            </Button>
                        )}
                    />
                </Box>
            </ModuleCard>

            <PatientExportDialog
                patientId={patient.id}
                open={exportOpen}
                onClose={() => setExportOpen(false)}
            />
            <PatientAnonymizeDialog
                patientId={patient.id}
                patientName={patient.name}
                open={anonymizeOpen}
                onClose={() => setAnonymizeOpen(false)}
                onDone={onChange}
            />
            <PatientDeleteDialog
                patientId={patient.id}
                patientName={patient.name}
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onDone={onChange}
                onSwitchToAnonymize={() => setAnonymizeOpen(true)}
            />
        </>
    );
}

export default PatientPrivacyPanel;
